import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCompany } from '../contexts/CompanyContext';
import { fetchCompanyCustomFields } from '../services/companyCustomizationApi';
import type { CompanyCustomField } from '../services/companyCustomizationApi';

type CustomFieldState = {
  fields: CompanyCustomField[];
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
};

const buildCacheKey = (companyId: string, module: string) => `${companyId}:${module}`;

const customFieldCache = new Map<string, CompanyCustomField[]>();

export const useCompanyCustomFields = (module: string): CustomFieldState => {
  const { currentCompany } = useCompany();
  const [fields, setFields] = useState<CompanyCustomField[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);

  const loadFields = useCallback(async () => {
    if (!currentCompany?.id || !module) {
      setFields([]);
      return;
    }

    const cacheKey = buildCacheKey(currentCompany.id, module);
    if (customFieldCache.has(cacheKey)) {
      setFields(customFieldCache.get(cacheKey) ?? []);
    }

    setIsLoading(true);
    setError(null);
    try {
      const data = await fetchCompanyCustomFields(currentCompany.id, module);
      const activeFields = data.filter((field) => field.isActive);
      customFieldCache.set(cacheKey, activeFields);
      setFields(activeFields);
    } catch (err) {
      console.error('[useCompanyCustomFields] Failed to load custom fields', err);
      setError('Não foi possível carregar os campos personalizados.');
      setFields([]);
    } finally {
      setIsLoading(false);
    }
  }, [currentCompany?.id, module]);

  useEffect(() => {
    loadFields();
  }, [loadFields, refreshToken]);

  const refresh = useCallback(async () => {
    if (!currentCompany?.id) return;
    const cacheKey = buildCacheKey(currentCompany.id, module);
    customFieldCache.delete(cacheKey);
    setRefreshToken((token) => token + 1);
  }, [currentCompany?.id, module]);

  return useMemo(
    () => ({
      fields,
      isLoading,
      error,
      refresh,
    }),
    [fields, isLoading, error, refresh],
  );
};


